import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@iconify/react';
import TimePeriodEditor from './TimePeriodEditor';

// Convert minutes to time string
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Convert click position to minutes
const positionToMinutes = (x, width) => {
  const ratio = x / width;
  const minutes = Math.round(ratio * 1440 / 5) * 5; // Snap to 5-minute intervals
  return Math.max(0, Math.min(1440, minutes));
};

export default function DayScheduleRow({ 
  dayName, 
  dayIndex, 
  daySchedule, 
  onChange,
  onCopyToAll,
  onCopyToWeekdays
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const gridRef = useRef(null);

  // Handle toggle day enabled
  const handleToggle = () => {
    onChange({
      ...daySchedule,
      enabled: !daySchedule.enabled
    });
  };

  // Handle adding a new period
  const handleAddPeriod = (startMinutes, endMinutes) => {
    const newPeriod = { 
      startMinutes: Math.min(startMinutes, endMinutes), 
      endMinutes: Math.max(startMinutes, endMinutes) 
    };
    
    // Merge overlapping periods
    const periods = [...daySchedule.periods, newPeriod];
    const merged = mergePeriods(periods);
    
    onChange({
      enabled: true, // Auto-enable when adding periods
      periods: merged
    });
  };

  // Merge overlapping periods
  const mergePeriods = (periods) => {
    if (periods.length === 0) return [];
    
    const sorted = [...periods].sort((a, b) => a.startMinutes - b.startMinutes);
    const merged = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const last = merged[merged.length - 1];
      const current = sorted[i];
      
      if (current.startMinutes <= last.endMinutes) {
        last.endMinutes = Math.max(last.endMinutes, current.endMinutes);
      } else {
        merged.push(current);
      }
    }
    
    return merged;
  };

  // Handle deleting a period
  const handleDeletePeriod = (periodIndex) => {
    const newPeriods = daySchedule.periods.filter((_, i) => i !== periodIndex);
    onChange({
      ...daySchedule,
      periods: newPeriods,
      enabled: newPeriods.length > 0 ? daySchedule.enabled : false
    });
    setEditingPeriod(null);
  };

  // Handle updating a period
  const handleUpdatePeriod = (periodIndex, newPeriod) => {
    const newPeriods = daySchedule.periods.map((p, i) => 
      i === periodIndex ? newPeriod : p
    );
    onChange({
      ...daySchedule,
      periods: mergePeriods(newPeriods)
    });
    setEditingPeriod(null);
  };

  // Mouse handlers for drag selection
  const handleMouseDown = (e) => {
    if (!gridRef.current) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const minutes = positionToMinutes(x, rect.width);
    
    setIsDragging(true);
    setDragStart(minutes);
    setDragEnd(minutes);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !gridRef.current) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const minutes = positionToMinutes(x, rect.width);
    
    setDragEnd(minutes);
  };

  const handleMouseUp = () => {
    if (isDragging && dragStart !== null && dragEnd !== null) {
      const start = Math.min(dragStart, dragEnd);
      const end = Math.max(dragStart, dragEnd);
      
      if (end - start >= 5) { // Minimum 5 minutes
        handleAddPeriod(start, end);
      }
    }
    
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  // Calculate period position and width
  const getPeriodStyle = (period) => {
    const left = (period.startMinutes / 1440) * 100;
    const width = ((period.endMinutes - period.startMinutes) / 1440) * 100;
    return { left: `${left}%`, width: `${width}%` };
  };

  // Get drag preview style
  const getDragStyle = () => {
    if (!isDragging || dragStart === null || dragEnd === null) return null;
    
    const start = Math.min(dragStart, dragEnd);
    const end = Math.max(dragStart, dragEnd);
    const left = (start / 1440) * 100;
    const width = ((end - start) / 1440) * 100;
    
    return { left: `${left}%`, width: `${width}%` };
  };

  const dragStyle = getDragStyle();

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      mb: 1,
      '&:hover': {
        bgcolor: 'grey.50'
      },
      borderRadius: 1,
      p: 0.5
    }}>
      {/* Day name and toggle */}
      <Box sx={{ 
        width: 140, 
        display: 'flex', 
        alignItems: 'center',
        gap: 1,
        flexShrink: 0
      }}>
        <Switch
          size="small"
          checked={daySchedule.enabled}
          onChange={handleToggle}
        />
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: daySchedule.enabled ? 'medium' : 'normal',
            color: daySchedule.enabled ? 'text.primary' : 'text.disabled'
          }}
        >
          {dayName}
        </Typography>
      </Box>

      {/* Schedule grid */}
      <Box 
        ref={gridRef}
        sx={{ 
          flex: 1, 
          height: 36,
          bgcolor: daySchedule.enabled ? 'grey.100' : 'grey.50',
          borderRadius: 1,
          position: 'relative',
          cursor: daySchedule.enabled ? 'crosshair' : 'default',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: daySchedule.enabled ? 'grey.300' : 'grey.200',
          opacity: daySchedule.enabled ? 1 : 0.5
        }}
        onMouseDown={daySchedule.enabled ? handleMouseDown : undefined}
        onMouseMove={daySchedule.enabled ? handleMouseMove : undefined}
        onMouseUp={daySchedule.enabled ? handleMouseUp : undefined}
        onMouseLeave={handleMouseUp}
      >
        {/* Hour grid lines */}
        {[3, 6, 9, 12, 15, 18, 21].map((hour) => (
          <Box
            key={hour}
            sx={{
              position: 'absolute',
              left: `${(hour / 24) * 100}%`,
              top: 0,
              bottom: 0,
              width: '1px',
              bgcolor: 'grey.200'
            }}
          />
        ))}

        {/* Existing periods */}
        {daySchedule.periods.map((period, index) => (
          <Tooltip 
            key={index}
            title={`${minutesToTime(period.startMinutes)} - ${minutesToTime(period.endMinutes)}`}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                bottom: 2,
                bgcolor: 'primary.main',
                borderRadius: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'primary.dark'
                },
                ...getPeriodStyle(period)
              }}
              onClick={(e) => {
                e.stopPropagation();
                setEditingPeriod(index);
              }}
            />
          </Tooltip>
        ))}

        {/* Drag preview */}
        {dragStyle && (
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              bottom: 2,
              bgcolor: 'primary.light',
              borderRadius: 0.5,
              opacity: 0.6,
              pointerEvents: 'none',
              ...dragStyle
            }}
          />
        )}
      </Box>

      {/* Actions menu */}
      <Box sx={{ ml: 1, flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <Icon icon="mdi:dots-vertical" width={18} />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={() => {
            handleAddPeriod(540, 1020); // 9am-5pm default
            setMenuAnchor(null);
          }}>
            <ListItemIcon>
              <Icon icon="mdi:plus" width={18} />
            </ListItemIcon>
            Add Business Hours (9-5)
          </MenuItem>
          <MenuItem onClick={() => {
            handleAddPeriod(0, 1440); // Full day
            setMenuAnchor(null);
          }}>
            <ListItemIcon>
              <Icon icon="mdi:clock-time-twelve" width={18} />
            </ListItemIcon>
            Add Full Day (24h)
          </MenuItem>
          <MenuItem onClick={() => {
            onCopyToAll();
            setMenuAnchor(null);
          }}>
            <ListItemIcon>
              <Icon icon="mdi:content-copy" width={18} />
            </ListItemIcon>
            Copy to All Days
          </MenuItem>
          <MenuItem onClick={() => {
            onCopyToWeekdays();
            setMenuAnchor(null);
          }}>
            <ListItemIcon>
              <Icon icon="mdi:calendar-week" width={18} />
            </ListItemIcon>
            Copy to Weekdays
          </MenuItem>
          <MenuItem onClick={() => {
            onChange({ enabled: false, periods: [] });
            setMenuAnchor(null);
          }}>
            <ListItemIcon>
              <Icon icon="mdi:delete" width={18} />
            </ListItemIcon>
            Clear Day
          </MenuItem>
        </Menu>
      </Box>

      {/* Period editor dialog */}
      {editingPeriod !== null && daySchedule.periods[editingPeriod] && (
        <TimePeriodEditor
          open={true}
          period={daySchedule.periods[editingPeriod]}
          onSave={(newPeriod) => handleUpdatePeriod(editingPeriod, newPeriod)}
          onDelete={() => handleDeletePeriod(editingPeriod)}
          onClose={() => setEditingPeriod(null)}
        />
      )}
    </Box>
  );
}

